/**
 * A two-dimensional map implemented as a `Map` of `Map`s.
 *
 * Allows storing and retrieving values using a pair of keys.
 *
 * @template K1 - The type of the first-level (outer) key.
 * @template K2 - The type of the second-level (inner) key.
 * @template V - The type of the value.
 *
 * @example
 * const map = new Map2D<string, number, string>();
 * map.set(["a", 1], "b");
 * console.log(map.get(["a", 1])); // "b"
 */
export class D2Map<K1, K2, V> implements Map<[K1, K2], V> {
  private _data: Map<K1, Map<K2, V>>;
  private _size: number;

  constructor(iterable?: Iterable<[[K1, K2], V]> | null) {
    this._data = new Map();
    this._size = 0;
    if (iterable != null) {
      for (const [keys, value] of iterable) {
        this.set(keys, value);
      }
    }
  }

  /**
   * Removes all entries from the map.
   */
  clear(): void {
    this._data.clear();
    this._size = 0;
  }

  /**
   * Removes a specified entry from the map.
   *
   * @param keys The keys of the entry to remove.
   *
   * @returns `true` if the entry was found and removed, `false` otherwise.
   */
  delete(keys: [K1, K2]): boolean {
    return d2MapDelete(this._data, keys[0], keys[1]);
  }

  /**
   * Returns an iterable of [keys, value] pairs for every entry in the map.
   *
   * @returns An iterable of [keys, value] pairs for every entry in the map.
   */
  entries(): MapIterator<[[K1, K2], V]> {
    return this[Symbol.iterator]();
  }

  /**
   * Executes a provided function once for each entry in the map.
   *
   * @param callbackFn The function to execute.
   *
   * @param thisArg (Optional) The value to use as `this` when executing the function.
   */
  forEach(
    callbackFn: (value: V, keys: [K1, K2], map: Map<[K1, K2], V>) => void,
    thisArg?: unknown,
  ): void {
    for (const [keys, value] of this) {
      callbackFn.call(thisArg, value, keys, this);
    }
  }

  /**
   * Gets the value associated with the given keys.
   *
   * @param key The keys to check.
   *
   * @returns The value if the keys exist, otherwise `undefined`.
   */
  get(keys: [K1, K2]): V | undefined {
    return d2MapGet(this._data, keys[0], keys[1]);
  }

  /**
   * Checks whether the given keys exist in the map.
   *
   * @param key The keys to check.
   *
   * @returns `true` if the keys exist, otherwise `false`.
   */
  has(keys: [K1, K2]): boolean {
    return d2MapHas(this._data, keys[0], keys[1]);
  }

  /**
   * Returns an iterable over the keys in the map.
   *
   * @returns An iterable over the keys in the map.
   */
  *keys(): MapIterator<[K1, K2]> {
    for (const [key1, map] of this._data) {
      for (const key2 of map.keys()) {
        yield [key1, key2];
      }
    }
  }

  /**
   * Adds a new keys-value pair to the map.
   *
   * @param key The keys to set.
   * @param value The value associated with the keys.
   *
   * @returns The map instance.
   */
  set(keys: [K1, K2], value: V): this {
    d2MapSet(this._data, keys[0], keys[1], value);
    return this;
  }

  /**
   * The number of entries in the map.
   */
  get size(): number {
    return this._size;
  }

  /**
   * Returns an iterable over the values in the map.
   *
   * @returns An iterable over the values in the map.
   */
  *values(): MapIterator<V> {
    for (const map of this._data.values()) {
      for (const value of map.values()) {
        yield value;
      }
    }
  }

  /**
   * Returns an iterable over the entries in the map.
   *
   * @returns An iterable over the entries in the map.
   */
  *[Symbol.iterator](): MapIterator<[[K1, K2], V]> {
    for (const [key1, map] of this._data) {
      for (const [key2, value] of map) {
        yield [[key1, key2], value];
      }
    }
  }

  /**
   * The default string tag.
   */
  get [Symbol.toStringTag](): string {
    return D2Map.name;
  }
}

/**
 * Removes a specified entry from the map.
 *
 * @param key1 The key for the outer map.
 * @param key2 The key for the inner map.
 *
 * @returns `true` if the entry was found and removed, `false` otherwise.
 */
export function d2MapDelete<K1, K2>(
  map2d: Map<K1, Map<K2, unknown>>,
  key1: K1,
  key2: K2,
): boolean {
  const map = map2d.get(key1);
  if (map == null || !map.delete(key2)) {
    return false;
  }
  if (map.size < 1) {
    map2d.delete(key1);
  }
  return true;
}

/**
 * Gets the value associated with the given keys.
 *
 * @param key1 The key for the outer map.
 * @param key2 The key for the inner map.
 *
 * @returns The value if the keys exist, otherwise `undefined`.
 */
export function d2MapGet<K1, K2, V>(
  map2d: Map<K1, Map<K2, V>>,
  key1: K1,
  key2: K2,
): V | undefined {
  return map2d.get(key1)?.get(key2);
}

/**
 * Checks whether the given keys exist in the map.
 *
 * @param key1 The key for the outer map.
 * @param key2 The key for the inner map.
 *
 * @returns `true` if the keys exist, otherwise `false`.
 */
export function d2MapHas<K1, K2, V>(
  map2d: Map<K1, Map<K2, V>>,
  key1: K1,
  key2: K2,
): boolean {
  return map2d.get(key1)?.has(key2) ?? false;
}

/**
 * Adds a new keys-value pair to the map.
 *
 * @param key1 The key for the outer map.
 * @param key2 The key for the inner map.
 *
 * @param value The value associated with the keys.
 */
export function d2MapSet<K1, K2, V>(
  map2d: Map<K1, Map<K2, V>>,
  key1: K1,
  key2: K2,
  value: V,
): void {
  let map1d = map2d.get(key1);
  if (map1d == null) {
    map1d = new Map();
    map2d.set(key1, map1d);
  }
  map1d.set(key2, value);
}
